/**
 * Owner Retention page — EX-C Step 5.
 *
 * Data-fetching wrapper around <RetentionPanel />. Owns:
 *   - threshold / offset state
 *   - useQuery for /api/owner/inactive-customers
 *   - refresh wiring (TanStack Query invalidation)
 *   - row-click → CRM customer profile
 *
 * Server scopes the response to JWT brand_id, so this page has no
 * brand selector and no brand path-param.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { assertBrandMatch } from '@/lib/utils';
import {
  PageHeader, FullSpinner, QueryError,
} from '@/components/ui_primitives/Misc';
import RetentionPanel from '@/components/RetentionPanel';

const PAGE_LIMIT = 50;

export default function OwnerRetentionPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [thresholdDays, setThresholdDays] = useState(30);
  const [offset, setOffset] = useState(0);

  const inactiveQ = useQuery({
    queryKey: qk.ownerInactive(thresholdDays, PAGE_LIMIT, offset),
    queryFn: async () =>
      assertBrandMatch(
        await api.ownerInactiveCustomers(token, thresholdDays, PAGE_LIMIT, offset),
        brandId,
      ),
    enabled: !!token && !!brandId,
    keepPreviousData: true,
  });

  // RetentionPanel passes (newThreshold, optionalNewOffset). The
  // pagination footer reuses the same handler so we can detect both
  // signatures: tab-click sends just the threshold (offset resets to 0);
  // prev/next sends (sameThreshold, explicitOffset).
  const handleThresholdChange = (newDays, newOffset) => {
    if (typeof newOffset === 'number') {
      setOffset(newOffset);
      return;
    }
    setThresholdDays(newDays);
    setOffset(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: qk.ownerInactive(thresholdDays, PAGE_LIMIT, offset),
    });
  };

  const handleCustomerClick = (item) => {
    if (!item?.customer_id) return;
    navigate(`/owner/crm/customers/${item.customer_id}`);
  };

  if (inactiveQ.isLoading) return <FullSpinner />;
  if (inactiveQ.error)
    return <QueryError error={inactiveQ.error} onRetry={() => inactiveQ.refetch()} />;

  const data = inactiveQ.data || {};

  return (
    <div className="space-y-8" data-testid="owner-retention-page">
      <PageHeader
        kicker="Retention"
        title="Customer Retention"
        subtitle={
          data.brand_name
            ? `Inactive customer detection for ${data.brand_name}.`
            : 'Inactive customer detection.'
        }
      />
      <RetentionPanel
        data={data}
        thresholdDays={thresholdDays}
        onThresholdChange={handleThresholdChange}
        onRefresh={handleRefresh}
        onCustomerClick={handleCustomerClick}
        isLoading={inactiveQ.isLoading}
        isFetching={inactiveQ.isFetching}
        showEmail={true}
      />
    </div>
  );
}
